import React, { useState, useCallback } from 'react'
import { styled, Menu, MenuItem } from '@mui/material'
import { PageTypeLabel } from '../../constants'
import { colorPalette } from '../../../../../../../style/colorPalette'
import AddIcon from '../../../../../../../../assets/add.svg'

type AddPageMenuProps = {
  pageTypeLabels: PageTypeLabel[]
  onPageTypeSelect: (pageTypeLabel: PageTypeLabel) => void
  disabled?: boolean
}

export const AddPageMenu: React.FC<AddPageMenuProps> = ({
  pageTypeLabels,
  onPageTypeSelect,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)

  const handleAddPageButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    },
    []
  )

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handlePageTypeSelect = useCallback(
    (pageTypeLabel: PageTypeLabel) => {
      onPageTypeSelect(pageTypeLabel)
      handleMenuClose()
    },
    [onPageTypeSelect, handleMenuClose]
  )

  return (
    <>
      <AddPageButton onClick={handleAddPageButtonClick} disabled={disabled}>
        <AddIconStyled src={AddIcon} alt="追加" />
        <AddPageText>ページを追加</AddPageText>
      </AddPageButton>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {pageTypeLabels.map(label => (
          <MenuItem key={label} onClick={() => handlePageTypeSelect(label)}>
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

const AddPageButton = styled('button')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  width: 'calc(100% - 16px)',
  minHeight: '96px',
  margin: '0 8px 8px',
  backgroundColor: colorPalette.monotone[0],
  border: `1.5px dashed ${colorPalette.monotone[2]}`,
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  flexShrink: 0,

  '&:hover:not(:disabled)': {
    borderColor: colorPalette.skyBlue[7],
    backgroundColor: colorPalette.monotone[1],
  },

  '&:active:not(:disabled)': {
    transform: 'scale(0.98)',
  },

  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
})

const AddIconStyled = styled('img')({
  width: '24px',
  height: '24px',
  opacity: 0.7,
})

const AddPageText = styled('span')({
  fontSize: '14px',
  color: colorPalette.monotone[4],
  fontWeight: 500,
  userSelect: 'none',
})
